import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Trophy, Crown, Check, X, Sparkles, Award, Users, Flame } from 'lucide-react';
import { PickupGame, AthleteProfile } from '../types';
import { triggerHaptic } from '../utils/haptics';
import { fireEliteSeriesConfetti } from '../utils/badgeEvaluator';

interface PlayerOfTheGameModalProps {
  game: PickupGame | null;
  user: AthleteProfile;
  isOpen: boolean;
  onClose: () => void;
  onVoteMVP: (gameId: string, playerId: string) => void;
}

export const PlayerOfTheGameModal: React.FC<PlayerOfTheGameModalProps> = ({
  game,
  user,
  isOpen,
  onClose,
  onVoteMVP,
}) => {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [justVotedPlayerName, setJustVotedPlayerName] = useState<string | null>(null);

  if (!isOpen || !game) return null;

  const mvpVotes = game.mvpVotes || {};
  const mvpVotedUserIds = game.mvpVotedUserIds || [];
  const hasUserVoted = mvpVotedUserIds.includes(user.id);

  // Total votes cast
  const totalVotes = Object.values(mvpVotes).reduce((sum, count) => sum + count, 0);

  // Find leader
  let currentLeaderId: string | null = null;
  let maxVotes = 0;
  Object.entries(mvpVotes).forEach(([pId, count]) => {
    if (count > maxVotes) {
      maxVotes = count;
      currentLeaderId = pId;
    }
  });

  const handleCastVote = (playerId: string, playerName: string) => {
    if (hasUserVoted) return;

    triggerHaptic('success');
    fireEliteSeriesConfetti();
    setSelectedPlayerId(playerId);
    setJustVotedPlayerName(playerName);

    onVoteMVP(game.id, playerId);

    setTimeout(() => {
      setJustVotedPlayerName(null);
    }, 3500);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-indigo-950 border-2 border-amber-400/50 text-white rounded-[2.5rem] p-6 max-w-lg w-full shadow-2xl space-y-6 relative overflow-hidden"
        >
          {/* Top Decorative Glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1.5 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 rounded-b-full shadow-[0_0_20px_rgba(251,191,36,0.8)]" />

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-5 right-5 p-2 rounded-full bg-indigo-900/80 hover:bg-indigo-800 text-indigo-300 hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="text-center space-y-2 pt-2">
            <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-amber-400/20 text-amber-300 border border-amber-400/40 shadow-inner">
              <Trophy className="w-8 h-8 text-amber-400 fill-amber-400 animate-bounce" />
            </div>

            <h2 className="text-2xl font-black italic uppercase tracking-tight text-white flex items-center justify-center gap-2">
              <span>Player of the Game MVP</span>
            </h2>

            <p className="text-xs text-indigo-200/80 max-w-sm mx-auto">
              Cast your vote for the most valuable player of "{game.title}" at {game.courtName}.
            </p>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-900/80 border border-white/10 text-[11px] font-bold text-amber-300">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span>{totalVotes} Total MVP Votes Cast</span>
            </div>
          </div>

          {/* Just Voted Toast Notification */}
          {justVotedPlayerName && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 bg-gradient-to-r from-amber-400 to-yellow-300 text-black font-extrabold text-xs rounded-xl shadow-lg flex items-center justify-center space-x-2 border border-black/20"
            >
              <Crown className="w-4 h-4 fill-black shrink-0" />
              <span>MVP Vote Cast for {justVotedPlayerName}! 🏆 (+25 XP)</span>
            </motion.div>
          )}

          {/* Candidates Roster List */}
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            <span className="text-[10px] text-indigo-300 font-black uppercase tracking-wider block">
              Participating Roster ({game.playersList.length} Athletes)
            </span>

            {game.playersList.map((player) => {
              const voteCount = mvpVotes[player.id] || 0;
              const votePercent = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
              const isLeader = currentLeaderId === player.id && voteCount > 0;
              const isUserSelf = player.id === user.id;

              return (
                <div
                  key={player.id}
                  className={`p-3.5 rounded-2xl border transition flex flex-col space-y-2 relative overflow-hidden ${
                    isLeader
                      ? 'bg-amber-400/10 border-amber-400/60 shadow-lg'
                      : 'bg-indigo-900/60 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <img
                          src={player.avatar}
                          alt={player.name}
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-indigo-950"
                          referrerPolicy="no-referrer"
                        />
                        {isLeader && (
                          <div className="absolute -top-2 -right-1 bg-amber-400 text-black p-0.5 rounded-full shadow-md">
                            <Crown className="w-3 h-3 fill-black" />
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-extrabold text-sm text-white">
                            {player.name}
                          </span>
                          {isUserSelf && (
                            <span className="text-[9px] bg-indigo-950 text-indigo-300 px-1.5 py-0.5 rounded font-mono border border-white/10">
                              YOU
                            </span>
                          )}
                          {isLeader && (
                            <span className="text-[9px] bg-amber-400 text-black font-black uppercase px-2 py-0.5 rounded-full flex items-center space-x-1">
                              <span>MVP Leader</span>
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-indigo-300/70 font-medium block">
                          RSVP: {player.rsvp.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <span className="text-sm font-black text-amber-300 block font-mono">
                          {voteCount} {voteCount === 1 ? 'vote' : 'votes'}
                        </span>
                        <span className="text-[10px] text-indigo-300/80 font-mono">
                          {votePercent}%
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleCastVote(player.id, player.name)}
                        disabled={hasUserVoted}
                        className={`px-3 py-1.5 rounded-xl font-black italic text-xs uppercase transition flex items-center space-x-1 ${
                          hasUserVoted
                            ? selectedPlayerId === player.id
                              ? 'bg-amber-400 text-black shadow-md'
                              : 'bg-indigo-950/80 text-indigo-400 border border-white/10 opacity-70 cursor-not-allowed'
                            : 'bg-amber-400 hover:bg-yellow-300 text-black shadow-md hover:scale-105 active:scale-95'
                        }`}
                      >
                        {hasUserVoted ? (
                          selectedPlayerId === player.id ? (
                            <>
                              <Check className="w-3.5 h-3.5 stroke-[3]" />
                              <span>Your Vote</span>
                            </>
                          ) : (
                            <span>Voted</span>
                          )
                        ) : (
                          <>
                            <Crown className="w-3.5 h-3.5 fill-black" />
                            <span>Vote MVP</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Percentage Progress Bar */}
                  <div className="w-full bg-indigo-950 h-1.5 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 rounded-full ${
                        isLeader ? 'bg-amber-400' : 'bg-indigo-400'
                      }`}
                      style={{ width: `${votePercent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="pt-2 border-t border-white/10 flex items-center justify-between text-xs text-indigo-300">
            <span className="flex items-center gap-1">
              <Award className="w-4 h-4 text-amber-400" />
              <span>MVP winner earns +100 Playground XP</span>
            </span>

            <button
              onClick={onClose}
              className="px-4 py-2 bg-indigo-900 hover:bg-indigo-800 text-white font-bold rounded-xl transition"
            >
              Done
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
